import React, { useState, useMemo } from 'react';
import { Play, Pause, RotateCcw, Info, AlertTriangle, CheckCircle2, Gauge } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · O6 · Voie d'insertion en centre ville
// Œuvre pédagogique interactive :
//  · Vue de dessus : boulevard 2 voies avec circulation en flux continu
//  · Voie d'insertion à droite avec voiture EGO
//  · 3 véhicules main-road animés (SMIL animateTransform) circulant à vitesse
//    ajustable par slider
//  · Bouton "Chercher un trou" → l'ego car se déplace pour matcher la vitesse
//    puis fusionne dans le gap disponible
//  · Panneau : les 3 clés (Clignotant → Allure → Trou), sanctions, pièges
// ══════════════════════════════════════════════════════════════════════════

const PHASES = [
  { key: 'attente',     label: 'Phase 1 · Observation',     color: '#3b82f6', hint: "J'active le clignotant gauche et j'observe le flux dans mon rétro extérieur gauche." },
  { key: 'acceleration', label: 'Phase 2 · Mise en allure', color: '#f59e0b', hint: "J'accélère sur la voie d'insertion pour matcher l'allure du flux. Pas trop vite, pas trop lentement." },
  { key: 'fusion',      label: 'Phase 3 · Fusion',          color: '#10b981', hint: "Je m'insère dans le trou repéré, sans forcer et sans faire freiner personne." },
  { key: 'insere',      label: 'Phase 4 · Inséré',          color: '#22c55e', hint: "Je suis sur la voie principale. Clignotant coupé. Contrôle rétro pour le suiveur." },
];

export default function InsertionCentreVilleSvg() {
  const [speed, setSpeed] = useState(50); // km/h flux principal
  const [phase, setPhase] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Position Y de l'ego selon la phase
  //  En France, l'insertion se fait de la BRETELLE (bas-droite) vers la VOIE DE DROITE
  //  du boulevard qui va vers l'EST. L'ego tourne progressivement de NW (sur bretelle)
  //  vers EST (fusionné). Voie de droite du boulevard = y=265 (sous la médiane y=220).
  const egoY = useMemo(() => {
    switch (phase) {
      case 0: return 480; // sur voie insertion, en attente
      case 1: return 400; // remonte la voie insertion
      case 2: return 320; // franchit la ligne de fusion
      case 3: return 265; // insérée sur voie de droite (sous la médiane)
      default: return 480;
    }
  }, [phase]);

  const egoX = useMemo(() => {
    switch (phase) {
      case 0: return 660;
      case 1: return 620;
      case 2: return 560;
      case 3: return 420;
      default: return 660;
    }
  }, [phase]);

  // Rotation : ego face au NW sur la bretelle, tourne vers l'EST pour fusionner
  // rotation 0 = front vers le NORD. -45 = NW. +90 = EST (direction du flux).
  const egoRotation = useMemo(() => {
    switch (phase) {
      case 0: return -35;  // NW (sur la bretelle inclinée)
      case 1: return -25;  // légèrement moins NW
      case 2: return 25;   // curving vers l'est
      case 3: return 90;   // aligné EST (direction du flux)
      default: return -35;
    }
  }, [phase]);

  // Durée d'animation des véhicules principaux (inversement proportionnel à la vitesse)
  const trafficDur = useMemo(() => Math.max(3, 12 - (speed / 10)), [speed]);

  const next = () => setPhase((p) => Math.min(3, p + 1));
  const reset = () => { setPhase(0); setPlaying(false); };

  // Autoplay
  React.useEffect(() => {
    if (!playing) return undefined;
    const t = setTimeout(() => {
      if (phase < 3) {
        setPhase(phase + 1);
      } else {
        setPlaying(false);
      }
    }, 1600);
    return () => clearTimeout(t);
  }, [playing, phase]);

  const currentPhase = PHASES[phase];

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {!playing ? (
          <button
            type="button"
            onClick={() => { if (phase >= 3) setPhase(0); setPlaying(true); }}
            data-testid="o6-play"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-emerald-500 text-emerald-950 font-bold text-[12px] hover:bg-emerald-400"
          >
            <Play size={13} /> Jouer la séquence
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(false)}
            data-testid="o6-pause"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-amber-500 text-amber-950 font-bold text-[12px]"
          >
            <Pause size={13} /> Pause
          </button>
        )}
        <button
          type="button"
          onClick={next}
          disabled={phase >= 3}
          data-testid="o6-next"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-[12px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Étape suivante →
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="o6-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold"
        >
          <RotateCcw size={13} /> Reset
        </button>
        <div className="ml-auto flex items-center gap-2 text-[11px] text-white/60 font-mono-spec">
          <Gauge size={13} />
          <span>Flux : {speed} km/h</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* Vue de dessus */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 700" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Insertion en centre ville">
            <defs>
              <linearGradient id="o6-road" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0f172a" />
              </linearGradient>
              <linearGradient id="o6-ego" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f5f5f5" />
                <stop offset="100%" stopColor="#94a3b8" />
              </linearGradient>
            </defs>

            {/* Fond immeubles (stylisé) */}
            <rect x={0} y={0} width={800} height={100} fill="#0b1120" />
            {[40, 140, 240, 340, 440, 540, 640, 740].map((x) => (
              <g key={x}>
                <rect x={x} y={30} width={70} height={70} fill="#1e293b" stroke="#0b1120" strokeWidth={1} />
                {/* Fenêtres */}
                <rect x={x + 10} y={40} width={12} height={12} fill="#fbbf24" opacity={0.35} />
                <rect x={x + 30} y={40} width={12} height={12} fill="#0b1120" />
                <rect x={x + 50} y={40} width={12} height={12} fill="#fbbf24" opacity={0.35} />
                <rect x={x + 10} y={60} width={12} height={12} fill="#0b1120" />
                <rect x={x + 30} y={60} width={12} height={12} fill="#fbbf24" opacity={0.35} />
                <rect x={x + 50} y={60} width={12} height={12} fill="#0b1120" />
              </g>
            ))}

            {/* Trottoir haut */}
            <rect x={0} y={100} width={800} height={30} fill="#475569" opacity={0.7} />

            {/* Boulevard principal (voies horizontales) — 2 voies + terre-plein */}
            <rect x={0} y={130} width={800} height={180} fill="url(#o6-road)" />
            {/* Ligne de voie centrale */}
            <line x1={0} y1={220} x2={800} y2={220} stroke="#fbbf24" strokeWidth={2.5} strokeDasharray="24 14" opacity={0.85} />
            {/* Ligne bord droite (bord de voie insertion — pointillé long car franchissable) */}
            <line x1={0} y1={310} x2={520} y2={310} stroke="#fff" strokeWidth={2.5} strokeDasharray="30 12" opacity={0.7} />
            {/* Terre-plein central (bordure blanche continue) */}
            <line x1={0} y1={130} x2={800} y2={130} stroke="#fff" strokeWidth={2} opacity={0.5} />

            {/* Trottoir bas + voie d'insertion à droite */}
            <rect x={0} y={310} width={800} height={30} fill="#475569" opacity={0.7} />

            {/* Voie d'insertion — trapèze qui rejoint le boulevard */}
            <polygon points="520,310 800,310 800,520 620,520" fill="url(#o6-road)" stroke="#fff" strokeWidth={2} strokeOpacity={0.4} />
            {/* Ligne insertion */}
            <line x1={620} y1={520} x2={520} y2={310} stroke="#fff" strokeWidth={2.5} strokeDasharray="16 10" opacity={0.6} />
            <text x={720} y={480} fill="#fbbf24" fontSize={11} fontWeight="bold" fontFamily="monospace" opacity={0.85}>VOIE INSERTION</text>

            {/* Flèche direction sur voie insertion */}
            <path d="M 700 470 L 640 380 M 640 380 L 660 388 M 640 380 L 656 366" stroke="#22c55e" strokeWidth={3} fill="none" opacity={0.6} />

            {/* Panneau "Cédez le passage" (triangle inversé) à l'entrée */}
            <g transform="translate(690 340)">
              <polygon points="0,-16 -14,10 14,10" fill="#fff" stroke="#dc2626" strokeWidth={4} />
              <text x={0} y={4} textAnchor="middle" fill="#dc2626" fontSize={10} fontWeight="900" fontFamily="system-ui">▼</text>
            </g>

            {/* VÉHICULES DU FLUX PRINCIPAL — voie de DROITE (y=265, sous la médiane à y=220) car ils vont vers l'EST (règle FR) */}
            {[
              { x0: -60, y: 265, color: '#3b82f6', delay: 0 },
              { x0: -160, y: 265, color: '#8b5cf6', delay: trafficDur * 0.4 },
              { x0: -260, y: 265, color: '#f97316', delay: trafficDur * 0.75 },
            ].map((v, i) => (
              <g key={i}>
                <rect x={-25} y={-15} width={50} height={30} rx={8} fill={v.color} stroke="#0b1120" strokeWidth={2}>
                  <animate attributeName="opacity" values="1" dur={`${trafficDur}s`} />
                </rect>
                <rect x={-19} y={-9} width={38} height={12} rx={2} fill="#0b1120" opacity={0.5} />
                <rect x={-23} y={-15} width={4} height={4} fill="#fbbf24" />
                <rect x={19} y={-15} width={4} height={4} fill="#fbbf24" />
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  from={`${v.x0} ${v.y}`}
                  to={`900 ${v.y}`}
                  dur={`${trafficDur}s`}
                  begin={`${-v.delay}s`}
                  repeatCount="indefinite"
                />
              </g>
            ))}

            {/* GAP repéré — cercle vert clignotant si phase >= 1 (positionné sur la voie de droite y=265) */}
            {phase >= 1 && (
              <g>
                <circle cx={450} cy={265} r={28} fill="none" stroke="#22c55e" strokeWidth={3} strokeDasharray="6 4">
                  <animate attributeName="stroke-opacity" values="1;0.3;1" dur="1s" repeatCount="indefinite" />
                  <animate attributeName="r" values="25;32;25" dur="1.5s" repeatCount="indefinite" />
                </circle>
                <text x={450} y={230} textAnchor="middle" fill="#22c55e" fontSize={10} fontWeight="bold">TROU REPÉRÉ</text>
              </g>
            )}

            {/* VOITURE EGO — position et rotation animées */}
            <g style={{ transition: 'transform 1.6s cubic-bezier(0.34, 1.56, 0.64, 1)' }} transform={`translate(${egoX} ${egoY}) rotate(${egoRotation})`}>
              {/* Carrosserie */}
              <rect x={-22} y={-38} width={44} height={76} rx={10} fill="url(#o6-ego)" stroke="#0b1120" strokeWidth={2.5} />
              {/* Pare-brise avant */}
              <path d="M -18,-30 L 18,-30 L 15,-6 L -15,-6 Z" fill="#0ea5e9" opacity={0.55} />
              {/* Lunette arrière */}
              <path d="M -18,28 L 18,28 L 15,6 L -15,6 Z" fill="#0ea5e9" opacity={0.45} />
              {/* Toit central */}
              <rect x={-15} y={-6} width={30} height={12} fill="#e2e8f0" />
              {/* Rétros */}
              <rect x={-27} y={-14} width={5} height={4} fill="#0ea5e9" />
              <rect x={22} y={-14} width={5} height={4} fill="#0ea5e9" />
              {/* Feux */}
              <rect x={-18} y={-40} width={5} height={3} fill="#fbbf24" />
              <rect x={13} y={-40} width={5} height={3} fill="#fbbf24" />
              <rect x={-18} y={37} width={5} height={3} fill="#dc2626" />
              <rect x={13} y={37} width={5} height={3} fill="#dc2626" />
              {/* Clignotant GAUCHE (phases 0-2) — clignote */}
              {phase < 3 && (
                <rect x={-22} y={-14} width={6} height={5} fill="#fbbf24">
                  <animate attributeName="opacity" values="1;0.1;1" dur="0.5s" repeatCount="indefinite" />
                </rect>
              )}
              <text x={0} y={5} textAnchor="middle" fill="#0b1120" fontSize={11} fontWeight="900">EGO</text>
            </g>

            {/* Traçage trajectoire projetée */}
            {phase >= 1 && phase < 3 && (
              <path
                d="M 660 470 Q 620 400 560 320 T 420 265"
                stroke="#22c55e"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                fill="none"
                opacity={0.6}
              >
                <animate attributeName="stroke-dashoffset" from="0" to="-40" dur="1.5s" repeatCount="indefinite" />
              </path>
            )}

            {/* PHASE INDICATOR */}
            <g transform="translate(30 570)">
              <text x={0} y={0} fill="#94a3b8" fontSize={11} fontWeight="bold" fontFamily="monospace">SÉQUENCE D&apos;INSERTION</text>
              {PHASES.map((p, i) => (
                <g key={p.key} transform={`translate(${i * 175} 15)`}>
                  <rect x={0} y={0} width={155} height={40} rx={8}
                        fill={i === phase ? p.color : '#1e293b'}
                        opacity={i === phase ? 0.35 : 0.6}
                        stroke={i === phase ? p.color : '#334155'}
                        strokeWidth={i === phase ? 2 : 1} />
                  <circle cx={16} cy={20} r={10} fill={i <= phase ? p.color : '#334155'} />
                  <text x={16} y={24} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">{i + 1}</text>
                  <text x={32} y={16} fill={i === phase ? '#fff' : '#94a3b8'} fontSize={9} fontWeight="bold" fontFamily="monospace">PHASE {i + 1}</text>
                  <text x={32} y={30} fill={i === phase ? '#fff' : '#64748b'} fontSize={9}>
                    {p.label.split('· ')[1]}
                  </text>
                </g>
              ))}
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 655)">
              <rect width={800} height={45} fill="#000" opacity={0.6} />
              <text x={20} y={28} fill="#fff" fontSize={13} fontWeight="bold">O6 · Voie d&apos;insertion centre ville</text>
              <text x={20} y={42} fill="#94a3b8" fontSize={11}>REMC C2d — S&apos;insérer dans un flux urbain</text>
              <circle cx={770} cy={22} r={16} fill="#dc2626" />
              <text x={770} y={27} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Slider vitesse flux */}
          <div className="p-4 border-t border-white/10 bg-black/40">
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="o6-speed" className="text-white/60 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
                Vitesse du flux principal
              </label>
              <span className="font-mono-spec text-white text-[13px] font-bold">
                {speed}<span className="text-white/40 text-[10px]"> km/h</span>
              </span>
            </div>
            <input
              id="o6-speed"
              type="range"
              min={30}
              max={80}
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              data-testid="o6-speed-slider"
              className="w-full accent-cyan-500 h-2"
              aria-label="Vitesse du flux"
            />
            <div className="mt-1 flex justify-between text-[10px] text-white/30 font-mono-spec">
              <span>30 km/h (dense)</span>
              <span>50 km/h (agglo)</span>
              <span>80 km/h (fluide)</span>
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="o6-panel">
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: currentPhase.color, background: currentPhase.color + '18' }}>
            <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: currentPhase.color }}>
              {currentPhase.label}
            </div>
            <p className="mt-1.5 text-white/90 text-[13px] leading-relaxed">
              {currentPhase.hint}
            </p>
          </div>

          <div className="mt-4 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Les 3 clés</div>
              <ol className="mt-1 text-white/85 text-[12.5px] leading-relaxed list-decimal ml-4 space-y-0.5">
                <li><span className="text-cyan-200 font-semibold">Clignotant</span> gauche activé DÈS l&apos;entrée sur la voie</li>
                <li><span className="text-cyan-200 font-semibold">Allure</span> à la vitesse du flux (jamais + lent)</li>
                <li><span className="text-cyan-200 font-semibold">Trou</span> repéré via rétro extérieur GAUCHE</li>
              </ol>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">
                Contrôle rétro G + coup d&apos;œil épaule G AVANT de braquer. On glisse dans le trou, on ne force pas.
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚠ Piège classique</div>
              <p className="mt-1 text-white/85 text-[12.5px] leading-relaxed">
                S&apos;insérer TROP LENTEMENT = faire freiner brutalement le suivant du flux → risque de collision arrière. La voie d&apos;insertion existe POUR accélérer, pas pour attendre.
              </p>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-red-500/40 bg-red-950/20 p-3 text-[11.5px] text-red-100 leading-relaxed">
            <span className="text-red-300 font-mono-spec text-[10px] uppercase tracking-wider font-bold block mb-0.5">Sanction refus de priorité</span>
            135 € · 4 points · rétention permis possible en cas d&apos;accident.
          </div>
        </div>
      </div>
    </div>
  );
}
